import { Check, Users, ArrowRight } from "lucide-react";
import {
    getCheckoutSession,
    getCustomerPortalSession,
} from "interfaces/stripe";
import { getMyInfo } from "interfaces/user";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "components/ui/Button";
import { Badge } from "components/ui/Badge";

type Offer = "chr" | "duckflow";

type Tier = {
    name: string;
    id: string;
    badge: string | null;
    subtitle: string;
    /** Big number; null for a plan on quote. */
    price: string | null;
    period: "/an" | "/mois";
    /** Line under the price (per-seat add-on, what is left out…). */
    priceNote: string | null;
    features: string[];
    featured: boolean;
    /** Enterprise is on quote: its button opens the support chat instead of a checkout. */
    onQuote?: boolean;
};

const OFFERS: Array<{ key: Offer; label: string }> = [
    { key: "chr", label: "Fournisseurs-CHR" },
    { key: "duckflow", label: "Duckflow" },
];

const HEADLINES: Record<Offer, string> = {
    chr: "Référencez votre entreprise auprès des professionnels CHR.",
    duckflow: "Toutes les données CHR en temps réel pour votre prospection dans une seule plateforme.",
};

const TIERS: Record<Offer, Tier[]> = {
    chr: [
        {
            name: "Référencement",
            id: "chr-listing",
            badge: null,
            subtitle: "Être présent dans l'annuaire",
            price: "25 €",
            period: "/an",
            priceNote: "Sans fiche, sans mise en avant",
            features: ["Référencement"],
            featured: false,
        },
        {
            name: "Fournisseur CHR",
            id: "chr-supplier",
            badge: "Recommandé",
            subtitle: "Être vu et choisi",
            price: "39 €",
            period: "/mois",
            priceNote: null,
            features: [
                "Référencement",
                "Lien dofollow",
                "Mise à jour de la fiche tous les mois",
                "Mise en avant dans les articles",
                "Certificat Fournisseur CHR",
            ],
            featured: true,
        },
    ],
    duckflow: [
        {
            name: "Duckflow",
            id: "duckflow",
            badge: null,
            subtitle: "La plateforme de prospection CHR",
            price: "129 €",
            period: "/mois",
            priceNote: "+ 39 € HT par siège /mois",
            features: [
                "Données CHR en temps réel",
                "Alertes temps réel",
                "API / MCP access",
                "Fournisseur CHR inclus",
            ],
            featured: true,
        },
        {
            name: "Enterprise",
            id: "enterprise",
            badge: null,
            subtitle: "Pour les grandes équipes commerciales",
            price: null,
            period: "/mois",
            priceNote: "Tarif adapté à votre équipe",
            features: [
                "Tout Duckflow",
                "API / MCP access",
                "Fournisseur CHR inclus",
                "Account manager dédié",
            ],
            featured: false,
            onQuote: true,
        },
    ],
};

export default function Pricing() {
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [params, setParams] = useSearchParams();
    const offer: Offer = params.get("offre") === "chr" ? "chr" : "duckflow";

    const setOffer = (next: Offer) => {
        const p = new URLSearchParams(params);
        p.set("offre", next);
        setParams(p, { replace: true });
    };

    const openCheckoutSession = async (plan: string) => {
        const res = await getCheckoutSession(plan);
        if (res?.url) window.open(res.url, "_blank");
    };

    const openCustomerPortal = async () => {
        const res = await getCustomerPortalSession();
        if (res?.url) window.open(res.url, "_blank");
    };

    useEffect(() => {
        getMyInfo().then((res) => {
            if (res.data) {
                setIsSubscribed(Boolean(res.data.subscriptionId?.length));
            }
        });
    }, []);

    const tiers = TIERS[offer];

    return (
        <div className="bg-canvas font-sans min-h-screen px-4 md:px-8 py-4">
            {/* Offer toggle */}
            <div className="flex justify-center mb-4">
                <div role="tablist" aria-label="Offre" className="inline-flex rounded-full bg-surface-sunken p-1">
                    {OFFERS.map((o) => (
                        <button
                            key={o.key}
                            role="tab"
                            aria-selected={offer === o.key}
                            onClick={() => setOffer(o.key)}
                            className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                offer === o.key ? "bg-surface shadow-card text-ink" : "text-ink-muted hover:text-ink"
                            }`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Header */}
            <div className="text-center mb-6">
                <h1 className="max-w-3xl mx-auto font-display text-2xl md:text-3xl font-normal tracking-tight leading-tight text-ink">
                    {HEADLINES[offer]}
                </h1>
                {isSubscribed && (
                    <Button variant="primary" className="mt-6" onClick={openCustomerPortal}>
                        Gérer l'abonnement
                        <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                )}
            </div>

            {!isSubscribed && (
                <div className="mx-auto max-w-4xl">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch">
                        {tiers.map((tier) => (
                            <div
                                key={tier.id}
                                className={`flex flex-col rounded-2xl ${
                                    tier.featured
                                        ? "bg-ink px-6 py-6"
                                        : "bg-surface border border-border px-6 py-6"
                                }`}
                            >
                                {/* Name (+ badge on the same line, so it costs no height) & audience */}
                                <div className="flex h-6 items-center justify-between gap-2">
                                    <h3
                                        className={`text-xl font-semibold leading-none ${
                                            tier.featured ? "text-ink-inverse" : "text-ink"
                                        }`}
                                    >
                                        {tier.name}
                                    </h3>
                                    {tier.badge && <Badge tone="accent">{tier.badge}</Badge>}
                                </div>
                                <p
                                    className={`mt-2 flex items-center gap-1.5 text-sm ${
                                        tier.featured ? "text-ink-inverse-muted" : "text-ink-muted"
                                    }`}
                                >
                                    <Users className="w-3.5 h-3.5 shrink-0" />
                                    {tier.subtitle}
                                </p>

                                {/* Divider */}
                                <div
                                    className={`border-t my-4 ${
                                        tier.featured ? "border-white/10" : "border-border"
                                    }`}
                                />

                                {/* Price */}
                                <div>
                                    <p
                                        className={`tabular-nums font-display text-4xl font-normal leading-none ${
                                            tier.featured ? "text-ink-inverse" : "text-ink"
                                        }`}
                                    >
                                        {tier.price ?? "Sur devis"}
                                        {tier.price && (
                                            <span
                                                className={`font-sans text-sm font-normal ml-0.5 ${
                                                    tier.featured ? "text-ink-inverse-muted" : "text-ink-muted"
                                                }`}
                                            >
                                                HT {tier.period}
                                            </span>
                                        )}
                                    </p>
                                    <p
                                        className={`mt-2 h-5 text-sm tabular-nums ${
                                            tier.featured ? "text-ink-inverse-muted" : "text-ink-muted"
                                        }`}
                                    >
                                        {tier.priceNote}
                                    </p>
                                </div>

                                {/* Features */}
                                <ul className="mt-4 space-y-2 flex-1">
                                    {tier.features.map((feature) => (
                                        <li
                                            key={feature}
                                            className={`flex items-center gap-2 text-sm ${
                                                tier.featured ? "text-ink-inverse-muted" : "text-ink"
                                            }`}
                                        >
                                            <Check className="w-3.5 h-3.5 shrink-0 text-success" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                {/* CTA */}
                                {tier.onQuote ? (
                                    // The support widget opens a new conversation on ?support=new.
                                    <a
                                        href="/pricing?support=new"
                                        className="mt-6 inline-flex h-11 w-full items-center justify-center rounded-lg border border-border-strong px-5 text-sm font-medium text-ink transition-colors duration-150 hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                                    >
                                        Nous contacter
                                    </a>
                                ) : tier.featured ? (
                                    <button
                                        onClick={() => openCheckoutSession(tier.id)}
                                        className="mt-6 w-full text-sm font-medium px-4 py-2.5 rounded-lg bg-ink-inverse text-ink hover:opacity-90 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                                    >
                                        Choisir ce plan
                                    </button>
                                ) : (
                                    <Button
                                        variant="primary"
                                        className="mt-6 w-full"
                                        onClick={() => openCheckoutSession(tier.id)}
                                    >
                                        Choisir ce plan
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>

                    <p className="mt-4 text-center text-xs text-ink-muted">
                        Tous les prix sont HT · Paiement sécurisé par Stripe
                    </p>
                </div>
            )}
        </div>
    );
}
